import { html } from "lit";
import memoize from 'memoize-one';
import { tooltip } from 'omni-ui/omni-tooltip-next.js';
import { OmniTooltipNextComponent } from "./omni-components";


export const exportStatusHtml = {

    /**
     * This function is used to get the HTML for export status
     * @param {Object} exportData - The value of the export status
     * @param {Object} htmlContent - Object containing the status, info, color and statusType
     * @param {String} errorDescription - The error description
     * @param {Function} onSyncErrorModalOpen - The function to open the sync error modal
     * @returns {String} - The export status html
    */
    getExportHTMLContent: (exportData, htmlContent, errorDescription, onSyncErrorModalOpen,statusType) => {
        let content = htmlContent.status;

        /*  if the export status is approved and export status not includes the given excluded status then we will show the approved status
            else we will show the export status */
        const isApproved = htmlContent.statusType === 'MOH' && exportData?.is_approved && !(['placement sent', 'campaign sent', 'accepted', 'rejected', 'export error', 'synced'].includes(htmlContent.status?.toLowerCase()));
        if (isApproved && content !== "In Platform") {
            content = html`
                <style>
                    :host span {
                        font-size: 14px !important;
                    }
                </style>
                <span part="invoker" aria-haspopup="true" style="color: #1a8978" ${tooltip(htmlContent.info)}>Approved</span>`;
        } else {
            if (exportData.title && exportData.title === "Publisher") {
                return html`<span></span>`;
            } else {
                content = html`
                <style>
                    :host::part(status-style):hover{
                        text-decoration: underline;
                    }
                    .error-message {
                        margin-top: 8px;
                    }
                    .error-item {
                        margin: 4px 0;
                    }
                    :host span {
                        font-size: 13px !important;
                        line-height: 16px !important;
                    }
                    :host::part(th-content) {
                        font-size: 12px !important;
                    }
                </style>
                <omni-tooltip-next place="left">
                <span slot="invoker" part="invoker ${statusType === 'MO' ? 'status-style' : '' }" aria-haspopup="true" style="color:${htmlContent.color}">
                    ${htmlContent.status}
                </span>
                <div slot="content">
                    <p>${htmlContent.info}</p>
                    ${htmlContent.status?.includes("Error") ? html`
                        <p class="error-message">
                            ${exportStatusHtml.formatErrorMessage(errorDescription)}
                        </p>
                    ` : ""}
                </div>
                </omni-tooltip-next>`;
            }
        }
        return html`
        <span part="export-status" @click="${() =>
            htmlContent.status && ['needs update','updated',"sync error", "in platform"].includes(htmlContent.status?.trim()?.toLowerCase()) && onSyncErrorModalOpen && onSyncErrorModalOpen(exportData)}">${content}</span>`;
    },


    /**
     * This function is used to show the export status in the table
     * @param {Object} value - The value of the export status
     * @param {String} statusType - The type of the status
     * @param {String} errorDescription - The error description
     * @param {Function} onSyncErrorModalOpen - The function to open the sync error modal
     * @returns {String} - The export status html
    */
    getExportHTML: memoize((exportData, statusType, errorDescription, onSyncErrorModalOpen = null) => {
        let errorStatus = ["needs approval", "sync error", "rejected", "synthetic id missing", "taxonomy error"];
        let commonStatus = ["not approved", "not exported", "sent to seller", "not synced"];

        let htmlContent = { status: "", info: "", color: "", statusType: statusType, };

        switch (statusType) {
            case "MOH":
                htmlContent.status = exportData?.mediaopshub_status?.trim();
                htmlContent.info = "MOH Status";
                break;
            case "MO":
                htmlContent.status = exportData?.mediaocean_status?.trim();
                htmlContent.info = "Buying System & Platform";
                break;
            case "TAXONOMY":
                htmlContent.status = exportData?.taxonomy_status?.trim();
                htmlContent.info = "Taxonomy Status";
                break;
            case "Delivered":
                htmlContent.status = exportData?.export_status?.trim();
                break;
            default:
                htmlContent.status = exportData?.export_status?.trim();
                break;
        }

        //Set color based on status
        if (errorStatus.includes(htmlContent.status?.toLowerCase()) || htmlContent.status?.includes("Error") || htmlContent.export_status === 'Updated' || htmlContent.export_status === 'Needs Update') {
            htmlContent.color = "#9b373c";
        } else if (commonStatus.includes(htmlContent.status?.toLowerCase())) {
            htmlContent.color = "#8b8d90";
        } else if (htmlContent.status) {
            htmlContent.color = "#1a8978";
        }
        return exportStatusHtml.getExportHTMLContent(exportData, htmlContent, errorDescription, onSyncErrorModalOpen,statusType);
    }),

    /**
     * Strips HTML tags and decodes HTML entities
     * @param {String} html - String containing HTML
     * @returns {String} Clean text without HTML
     */
    cleanHtmlMessage: (htmlString) => {
        if (!htmlString) return '';
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        return doc.body.textContent || '';
    },

    /**
     * Format taxonomy specific error messages
     * @param {String} message - Raw taxonomy error message
     * @returns {String} Formatted message
     */
    formatTaxonomyError: (message) => {
        if (!message) return '';
        const errors = message.split(';')
            .map(err => err.trim())
            .filter(Boolean);
        const formattedErrors = errors.map(err => {
            switch (err.toLowerCase()) {
                case 'synthetic id error':
                    return 'Synthetic ID is missing or invalid';
                case 'invalid taxonomy':
                    return 'Taxonomy validation failed';
                default:
                    return err;
            }
        });
        return formattedErrors.join('\n');
    },

    /**
     * Format error messages to be human readable
     * @param {String} errorDescription - Raw error description
     * @returns {String} Formatted error message
     */
    formatErrorMessage: (errorDescription) => {
        try {
            if (typeof errorDescription === 'string' && 
                (errorDescription.includes('&lt;') || errorDescription.includes('<'))) {
                    return exportStatusHtml.cleanHtmlMessage(errorDescription);
            }

            if (typeof errorDescription === 'string' && 
                (errorDescription.includes('Taxonomy') || errorDescription.includes('Synthetic'))) {
                return exportStatusHtml.formatTaxonomyError(errorDescription);
            }

            const errorObj = typeof errorDescription === 'string' ? 
                JSON.parse(errorDescription) : errorDescription;

            if (errorObj.Code && errorObj.Message) {
                switch (errorObj.Code) {
                    case 403: return "Access Denied: Please contact your administrator.";
                    case 404: return "The requested item could not be found.";
                    case 500: return "Please try again later or contact support.";
                    default: return `Error ${errorObj.Code}: ${errorObj.Message}`;
                }
            }

            if (Array.isArray(errorObj)) {
                return errorObj.map(error => 
                    `${error.fieldName || ''}: ${error.message || ''}`).join('\n');
            }

            return errorObj.Message || errorObj.message || 
                   JSON.stringify(errorObj, null, 2);

        } catch (e) {
            return exportStatusHtml.cleanHtmlMessage(errorDescription) || "An unknown error occurred";
        }
    },

        getExportHTMLContentAgGrid: (exportData, htmlContent, errorDescription, onSyncErrorModalOpen, statusType) => {
            let content = htmlContent.status;
    
            const isApproved = htmlContent.statusType === 'MOH' && exportData?.is_approved && !(['placement sent', 'campaign sent', 'accepted', 'rejected', 'export error', 'synced'].includes(htmlContent.status?.toLowerCase()));
            if (isApproved && content !== "In Platform") {
                content = (
                    <span
                        part="invoker"
                        aria-haspopup="true"
                        style={{ color: "#1a8978", fontSize: "14px" }}
                        title={htmlContent.info}
                    >
                        Approved
                    </span>
                );
            } else {
                if (exportData.title && exportData.title === "Publisher") {
                    // For Publisher, just render a simple span (no lit-html)
                    content = (
                        <span></span>
                    );
                    return content;
                } else {
                    content = (
                        <OmniTooltipNextComponent place="left">
                            <span
                                slot="invoker"
                                part={`invoker ${statusType === 'MO' ? 'status-style' : ''}`}
                                aria-haspopup="true"
                                style={{ color: htmlContent.color }}
                            >
                                {htmlContent.status}
                            </span>
                            <div slot="content">
                                <p>{htmlContent.info}</p>
                                {htmlContent.status?.includes("Error") && (
                                    <p className="error-message">
                                        {exportStatusHtml.formatErrorMessage(errorDescription)}
                                    </p>
                                )}
                            </div>
                        </OmniTooltipNextComponent>
                    );
                }
            }
            return (
                <span
                    style={{ cursor: htmlContent.status && ['needs update', 'updated', "sync error", "in platform"].includes(htmlContent.status?.trim()?.toLowerCase()) && onSyncErrorModalOpen ? 'pointer' : 'default' }}
                    part="export-status"
                >
                    {content}
                </span>
            );
        },
    
        getExportHTMLAgGrid: (exportData, statusType, errorDescription, onSyncErrorModalOpen = null) => {
            let errorStatus = ["needs approval", "sync error", "rejected", "synthetic id missing", "taxonomy error"];
            let commonStatus = ["not approved", "not exported", "sent to seller", "not synced"];
    
            let htmlContent = { status: "", info: "", color: "", statusType: statusType, };
    
            switch (statusType) {
                case "MOH":
                    htmlContent.status = exportData?.mediaopshub_status?.trim();
                    htmlContent.info = "MOH Status";
                    break;
                case "MO":
                    htmlContent.status = exportData?.mediaocean_status?.trim();
                    htmlContent.info = "Buying System & Platform";
                    break;
                case "TAXONOMY":
                    htmlContent.status = exportData?.taxonomy_status?.trim();
                    htmlContent.info = "Taxonomy Status";
                    break;
                case "Delivered":
                    htmlContent.status = exportData?.export_status?.trim();
                    break;
                default:
                    htmlContent.status = exportData?.export_status?.trim();
                    break;
            }
    
            //Set color based on status
            if (errorStatus.includes(htmlContent.status?.toLowerCase()) || htmlContent.status?.includes("Error") || htmlContent.export_status === 'Updated' || htmlContent.export_status === 'Needs Update') {
                htmlContent.color = "#9b373c";
            } else if (commonStatus.includes(htmlContent.status?.toLowerCase())) {
                htmlContent.color = "#8b8d90";
            } else if (htmlContent.status) {
                htmlContent.color = "#1a8978";
            }

            return exportStatusHtml.getExportHTMLContentAgGrid(exportData, htmlContent, errorDescription, onSyncErrorModalOpen, statusType);
        }
}


